import { supabase, SupabaseAssistantUtils } from "@/lib";
import { create } from "zustand";
import { persist } from "zustand/middleware";
import { useAuthStore } from "./authStore";
import useModelStore from "./modelStore";
import { getDefaultAvatarUrl } from "@/lib/utils/supabaseUrl";

// Helper function to find the earliest created workspace
const findEarliestWorkspace = (workspaces) => {
  if (!workspaces || workspaces.length === 0) return null;

  // Sort by created_at date (earliest first) and return the first one
  return (
    workspaces
      .filter((w) => w && w.created_at) // Filter out any invalid workspaces
      .sort((a, b) => new Date(a.created_at) - new Date(b.created_at))[0] ||
    workspaces[0]
  );
};

const STORAGE_KEY = "assistant-storage";

const INITIAL_STATE = {
  initialized: false,
  initializing: false,
  error: null,
  initializedFromOnboarding: false,
  assistants: [],
  currentAssistant: null,
  currentAssistantId: null,
  currentAssistantTitle: null,
  workspaceAssistants: new Map(),

  assistantTools: [],
  files: new Map(),
  assistantVectorStores: new Map(),
  isLoading: false,

  vectorStores: new Map(),
  vectorStoreFiles: new Map(),
};

export const useAssistantStore = create(
  persist(
    (set, get) => ({
      ...INITIAL_STATE,

      setCurrentAssistant: (assistant) => {
        set({
          currentAssistant: assistant,
          currentAssistantId: assistant?.id || null,
          currentAssistantTitle: assistant?.name || null,
        });
      },
      setInitializedFromOnboarding: (value) => {
        set({ initializedFromOnboarding: value });
      },
      setCurrentAssistantId: (assistantId) => {
        set({ currentAssistantId: assistantId });
      },
      setCurrentAssistantTitle: (title) => {
        set({ currentAssistantTitle: title });
      },
      setAssistants: (assistants) => {
        set({ assistants });
      },

      // NEW: Create temporary default assistant for onboarding users
      createTemporaryDefaultAssistant: async (userDisplayName = "User") => {
        try {
          const {
            userId,
            currentWorkspaceId,
            getPersonalWorkspaceId,
            setCurrentWorkspaceId,
            setWorkspaces,
          } = useAuthStore.getState();

          if (!userId) {
            throw new Error(
              "User ID is required to create temporary assistant"
            );
          }

          let workspaceId = getPersonalWorkspaceId() || currentWorkspaceId;

          // If no workspace exists, fetch or create one
          if (!workspaceId) {
            console.log(
              "🔄 No workspace found, creating/fetching workspace for temporary assistant..."
            );

            try {
              // Try to fetch existing workspaces first
              const { data: workspacesData, error: fetchError } = await supabase
                .from("workspaces")
                .select("*")
                .eq("user_id", userId);

              if (fetchError) {
                console.error("Error fetching workspaces:", fetchError);
              }

              if (workspacesData && workspacesData.length > 0) {
                // Use existing workspace
                const personalWorkspace =
                  workspacesData.find((w) => w.is_personal) ||
                  workspacesData[0];
                workspaceId = personalWorkspace.id;
                setCurrentWorkspaceId(workspaceId);
                setWorkspaces(workspacesData);
                console.log(
                  "✅ Found existing workspace for temporary assistant"
                );
              } else {
                // Create a new personal workspace
                const { data: newWorkspace, error: createError } =
                  await supabase
                    .from("workspaces")
                    .insert([
                      {
                        user_id: userId,
                        owner_id: userId,
                        name: "Personal Project",
                        is_personal: true,
                        workspace_members: [userId],
                        metadata: {
                          created_at: new Date().toISOString(),
                          created_by: userId,
                          stats: {
                            chat_count: 0,
                            file_count: 0,
                            member_count: 1,
                          },
                        },
                      },
                    ])
                    .select()
                    .single();

                if (createError) {
                  console.error("Error creating workspace:", createError);
                  throw new Error(
                    `Failed to create workspace: ${createError.message}`
                  );
                }

                if (newWorkspace) {
                  workspaceId = newWorkspace.id;
                  setCurrentWorkspaceId(workspaceId);
                  setWorkspaces([newWorkspace]);
                  console.log(
                    "✅ Created new workspace for temporary assistant"
                  );
                } else {
                  throw new Error(
                    "Failed to create workspace - no data returned"
                  );
                }
              }
            } catch (workspaceError) {
              console.error("Error setting up workspace:", workspaceError);
              throw new Error(
                `Workspace setup failed: ${workspaceError.message}`
              );
            }
          }

          if (!workspaceId) {
            throw new Error(
              "Failed to obtain workspace ID for temporary assistant"
            );
          }

          // Create a simple temporary assistant
          const tempAssistant = {
            id: `temp_${Date.now()}_${userId}`, // Unique temporary ID
            name: `${userDisplayName}'s Personal AI`,
            description:
              "Your personal AI assistant (setting up your experience...)",
            model: "gpt-4.1-mini",
            instructions: `You are ${userDisplayName}'s personal AI assistant. You are helpful, friendly, and knowledgeable. Please note that your full capabilities are still being set up, but I'm ready to help you with any questions or tasks.`,
            user_id: userId,
            workspace_id: workspaceId,
            workspace_ids: [workspaceId],
            tools: [
              {
                type: "function",
                function: {
                  name: "web_search",
                  description: "Search the web for real-time information",
                  parameters: {
                    type: "object",
                    properties: {
                      query: {
                        type: "string",
                        description: "The search query to look up on the web",
                      },
                    },
                    required: ["query"],
                  },
                },
              },
            ],
            tool_names: ["web_search"],
            metadata: {
              user_id: userId,
              workspace_id: workspaceId,
              created_from: "temporary_onboarding",
              created_at: new Date().toISOString(),
              is_temporary: "true",
              is_default: "true",
            },
            avatar_url: getDefaultAvatarUrl(),
            image_url: getDefaultAvatarUrl(),
            is_default: true,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString(),
          };

          // Set the temporary assistant in the store
          set({
            assistants: [tempAssistant],
            currentAssistant: tempAssistant,
            currentAssistantId: tempAssistant.id,
            currentAssistantTitle: tempAssistant.name,
            initialized: true,
            initializing: false,
            error: null,
          });

          console.log(`🚀 Created temporary assistant: ${tempAssistant.name}`);
          return tempAssistant;
        } catch (error) {
          console.error("❌ Error creating temporary assistant:", error);
          throw error;
        }
      },

      // NEW: Replace temporary assistant with real assistants
      replaceTemporaryAssistant: (realAssistants) => {
        const { assistants, currentAssistant } = get();

        // Check if current assistant is temporary OR if we have any temporary assistants in the array
        const isCurrentTemporary =
          currentAssistant?.metadata?.is_temporary === "true";
        const temporaryAssistants = assistants.filter(
          (a) => a.metadata?.is_temporary === "true"
        );
        const hasTemporaryAssistants = temporaryAssistants.length > 0;

        if (
          (isCurrentTemporary || hasTemporaryAssistants) &&
          realAssistants &&
          realAssistants.length > 0
        ) {
          // Find the default assistant from real assistants
          const defaultAssistant =
            realAssistants.find((a) => a.is_default) || realAssistants[0];

          // Remove temporary assistants from existing assistants array
          const nonTemporaryAssistants = assistants.filter(
            (a) => a.metadata?.is_temporary !== "true"
          );

          // Combine non-temporary existing assistants with new real assistants (avoiding duplicates)
          const existingIds = new Set(nonTemporaryAssistants.map((a) => a.id));
          const newRealAssistants = realAssistants.filter(
            (a) => !existingIds.has(a.id)
          );
          const combinedAssistants = [
            ...nonTemporaryAssistants,
            ...newRealAssistants,
          ];

          console.log(
            `🔄 Replacing ${temporaryAssistants.length} temporary assistant(s) with ${newRealAssistants.length} real assistant(s). Setting ${defaultAssistant.name} as current.`
          );

          set({
            assistants: combinedAssistants,
            currentAssistant: defaultAssistant,
            currentAssistantId: defaultAssistant.id,
            currentAssistantTitle: defaultAssistant.name,
          });

          return true;
        }

        return false;
      },

      initializeAssistants: async () => {
        const { userId, currentWorkspaceId, user } = useAuthStore.getState();
        const { initializedFromOnboarding, assistants } = get();

        // If initialized from onboarding AND we already have assistants, return early
        if (initializedFromOnboarding && assistants && assistants.length > 0) {
          // Check if we only have a temporary assistant and need to load real ones
          const hasOnlyTemporary =
            assistants.length === 1 &&
            assistants[0]?.metadata?.is_temporary === "true";

          if (hasOnlyTemporary) {
            console.log(
              "🔄 Only temporary assistant found, loading real assistants from database..."
            );
            // Continue with normal initialization to load real assistants
          } else {
            // Make sure we have a current assistant set
            const { currentAssistant } = get();
            if (!currentAssistant) {
              const defaultAssistant =
                assistants.find((a) => a.is_default) || assistants[0];
              if (defaultAssistant) {
                set({
                  currentAssistant: defaultAssistant,
                  currentAssistantId: defaultAssistant.id,
                  currentAssistantTitle: defaultAssistant.name,
                });
                console.log(
                  `🎯 Set ${defaultAssistant.name} as current assistant during initialization`
                );
              }
            }
            set({ initialized: true, initializing: false });
            return assistants;
          }
        }

        // If initialized from onboarding but no assistants, we need to load them
        if (
          initializedFromOnboarding &&
          (!assistants || assistants.length === 0)
        ) {
          console.log(
            "🔄 Onboarding completed but no assistants in store, loading from database..."
          );
          // Continue with normal initialization to load assistants
        }

        try {
          set({ initializing: true, error: null });

          const userIdToUse = userId || user?.id;

          if (!userIdToUse) {
            const retryPromise = new Promise((resolve) => {
              setTimeout(async () => {
                const {
                  userId: retryUserId,
                  user: retryUser,
                  currentWorkspaceId: retryWorkspaceId,
                } = useAuthStore.getState();

                const finalUserId = retryUserId || retryUser?.id;

                if (finalUserId) {
                  try {
                    const result = await get().initializeAssistants();
                    resolve(result);
                  } catch (retryError) {
                    resolve([]);
                  }
                } else {
                  set({
                    assistants: [],
                    currentAssistant: null,
                    currentAssistantId: null,
                    currentAssistantTitle: null,
                    vectorStores: new Map(),
                    vectorStoreFiles: new Map(),
                    assistantVectorStores: new Map(),
                    initialized: true,
                  });
                  resolve([]);
                }
              }, 2000);
            });

            return retryPromise;
          }

          if (!currentWorkspaceId) {
            try {
              const { workspaces } = useAuthStore.getState();

              if (workspaces && workspaces.length > 0) {
                const workspace = findEarliestWorkspace(workspaces);

                useAuthStore.getState().setCurrentWorkspaceId(workspace.id);
              } else {
                const { data: workspacesData } = await supabase
                  .from("workspaces")
                  .select("*")
                  .eq("user_id", userIdToUse);

                if (workspacesData && workspacesData.length > 0) {
                  const workspace = findEarliestWorkspace(workspacesData);

                  useAuthStore.getState().setWorkspaces(workspacesData);
                  useAuthStore.getState().setCurrentWorkspaceId(workspace.id);
                }
              }
            } catch (workspaceError) {
              console.error(
                "[AssistantStore] Error fetching workspaces:",
                workspaceError
              );
            }
          }

          const updatedWorkspaceId = useAuthStore.getState().currentWorkspaceId;

          // Only fetch Supabase data since we no longer use OpenAI assistants

          const supabaseData = await Promise.allSettled([
            SupabaseAssistantUtils.listAssistants({
              userId: userIdToUse,
              workspaceId: updatedWorkspaceId,
            }),
          ]);

          try {
            // Check if the promise was rejected (database connection error)
            const assistantsResult = supabaseData[0];

            if (assistantsResult.status === "rejected") {
              console.error(
                "Failed to fetch assistants from database:",
                assistantsResult.reason
              );

              // Check if it's a network/connection error
              const errorMessage =
                assistantsResult.reason?.message || "Unknown error";
              const isConnectionError =
                errorMessage.toLowerCase().includes("network") ||
                errorMessage.toLowerCase().includes("connection") ||
                errorMessage.toLowerCase().includes("fetch") ||
                errorMessage.toLowerCase().includes("failed to fetch") ||
                errorMessage.toLowerCase().includes("timeout") ||
                assistantsResult.reason?.code === "PGRST116" ||
                (typeof navigator !== "undefined" && !navigator.onLine);

              set({
                assistants: [],
                currentAssistant: null,
                currentAssistantId: null,
                currentAssistantTitle: null,
                initialized: true,
                initializing: false,
                error: isConnectionError
                  ? "Unable to connect to database. Please check your internet connection and try again."
                  : `Failed to load assistants: ${errorMessage}`,
              });

              return [];
            }

            // Use the filtered assistants directly since they already have proper tools
            const assistants = assistantsResult.value || [];

            // If we have real assistants and currently have a temporary one, replace it
            if (assistants && assistants.length > 0) {
              const wasReplaced = get().replaceTemporaryAssistant(assistants);
              if (wasReplaced) {
                set({ initialized: true, error: null, initializing: false });
                return assistants;
              }
            }

            // Find default assistant or use first one
            let defaultAssistant = assistants.find(
              (assistant) => assistant.is_default === true
            );

            const currentAssistant = defaultAssistant || assistants[0] || null;

            set({
              assistants: assistants,
              currentAssistant: currentAssistant,
              currentAssistantId: currentAssistant?.id || null,
              currentAssistantTitle: currentAssistant?.name || null,
              initialized: true,
              error: null,
              initializing: false,
            });

            try {
              const workspaceAssistantsMap = new Map();

              for (const assistant of assistants) {
                if (assistant && Array.isArray(assistant.workspace_ids)) {
                  assistant.workspace_ids.forEach((workspaceId) => {
                    if (!workspaceAssistantsMap.has(workspaceId)) {
                      workspaceAssistantsMap.set(workspaceId, []);
                    }
                    workspaceAssistantsMap.get(workspaceId).push(assistant.id);
                  });
                }
              }

              set({ workspaceAssistants: workspaceAssistantsMap });
            } catch (error) {
              // Error setting workspace assistants - handled silently
            }

            return assistants;
          } catch (formatError) {
            set({
              assistants: [],
              currentAssistant: null,
              currentAssistantId: null,
              currentAssistantTitle: null,
              vectorStores: new Map(),
              vectorStoreFiles: new Map(),
              assistantVectorStores: new Map(),
              initialized: true,
              error: `Error formatting assistants: ${formatError.message}`,
              initializing: false,
            });

            return [];
          }
        } catch (error) {
          console.error("Assistant initialization error:", error);

          set({
            error: `Assistant initialization failed: ${error.message}`,
            initialized: true,
            initializing: false,
            assistants: [],
            currentAssistant: null,
            currentAssistantId: null,
            currentAssistantTitle: null,
          });

          return [];
        } finally {
          set({ initializing: false });
        }
      },

      createAssistantWithConfig: async (config) => {
        const {
          currentWorkspace,
          currentWorkspaceId,
          user,
          userId,
          getPersonalWorkspaceId,
        } = useAuthStore.getState();
        set({ isLoading: true, error: null });

        try {
          if (!userId) {
            console.warn(
              "No user ID provided from auth store, attempting to get it from user object"
            );
            if (!user?.id) {
              throw new Error(
                "User ID is required to create an assistant. Please ensure you're logged in."
              );
            }
          }

          const userIdToUse = userId || user?.id || "";

          // Get workspace_id, prioritizing personal workspace
          const personalWorkspaceId = getPersonalWorkspaceId();
          const workspaceIdToUse =
            config.workspace_id || personalWorkspaceId || currentWorkspaceId;

          const {
            name,
            description,
            model = "gpt-4.1-mini",
            instructions,
            tools = [],
            files = [],
            metadata = {
              user_id: userIdToUse,
              workspace_id: workspaceIdToUse,
              created_at: new Date().toISOString(),
            },
          } = config;

          const toolNames = tools
            .map((tool) => {
              if (tool.type === "function") {
                return tool.function?.name;
              }
              return tool.type;
            })
            .filter(Boolean);

          const enhancedMetadata = {
            ...metadata,
            user_id: userIdToUse,
            workspace_id: metadata.workspace_id || workspaceIdToUse,
            created_at: metadata.created_at || new Date().toISOString(),
            is_default:
              config.is_default || metadata.is_default === "true"
                ? "true"
                : "false",
          };

          // Determine if this is a personal assistant
          const isPersonalAssistant = Boolean(
            config.is_default ||
              metadata.is_default === "true" ||
              metadata.is_default === true ||
              (config.name &&
                (config.name.toLowerCase().includes("personal") ||
                  config.name.includes("'s Personal AI") ||
                  config.name.includes("'s Assistant")))
          );

          const supabaseAssistantData = {
            name,
            description: description || "",
            model,
            instructions: instructions || "",
            user_id: userIdToUse,
            workspace_id: workspaceIdToUse, // Use the determined workspace ID
            workspace_ids: [workspaceIdToUse], // Array of workspace IDs
            tools: tools,
            tool_names: toolNames,
            metadata: enhancedMetadata,
            avatar_url: config.avatar_url || config.image_url || "",
            image_url: config.image_url || config.avatar_url || "",
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString(),
            is_default: Boolean(
              config.is_default || metadata.is_default === "true"
            ), // Ensure boolean
            // is_default: isPersonalAssistant, // Add is_personal field
          };

          console.log("💾 [AssistantStore] Creating assistant with data:", {
            name: supabaseAssistantData.name,
            model: supabaseAssistantData.model,
            user_id: supabaseAssistantData.user_id,
            workspace_id: supabaseAssistantData.workspace_id,
            workspace_ids: supabaseAssistantData.workspace_ids,
            tools_count: supabaseAssistantData.tools?.length || 0,
            tool_names: supabaseAssistantData.tool_names,
            has_avatar: !!supabaseAssistantData.avatar_url,
            is_default: supabaseAssistantData.is_default,
            // is_personal: supabaseAssistantData.is_personal,
          });

          const { data: supabaseAssistant, error: insertError } = await supabase
            .from("assistants")
            .insert(supabaseAssistantData)
            .select()
            .single();

          if (insertError) {
            console.error(
              "❌ [AssistantStore] Error creating Supabase assistant:",
              insertError
            );
            console.error(
              "❌ [AssistantStore] Failed data payload:",
              supabaseAssistantData
            );
            throw new Error(
              `Failed to create assistant in database: ${insertError.message}`
            );
          }

          if (!supabaseAssistant) {
            throw new Error(
              "Assistant was not returned from database after creation"
            );
          }

          console.log(
            "✅ [AssistantStore] Successfully created assistant in Supabase:",
            {
              id: supabaseAssistant.id,
              name: supabaseAssistant.name,
              user_id: supabaseAssistant.user_id,
              workspace_id: supabaseAssistant.workspace_id,
            }
          );

          const combinedAssistant = supabaseAssistant;

          set((state) => {
            const existingIndex = state.assistants.findIndex(
              (a) => a.id === combinedAssistant.id
            );

            const updatedAssistants = [...state.assistants];
            if (existingIndex !== -1) {
              updatedAssistants[existingIndex] = combinedAssistant;
              console.log(
                "🔄 [AssistantStore] Updated existing assistant in store"
              );
            } else {
              updatedAssistants.unshift(combinedAssistant);
              console.log("➕ [AssistantStore] Added new assistant to store");
            }

            // Set as current assistant if it's marked as default or if no current assistant exists
            const shouldSetAsCurrent =
              combinedAssistant.is_default || !state.currentAssistant;

            const newState = {
              assistants: updatedAssistants,
            };

            if (shouldSetAsCurrent) {
              newState.currentAssistant = combinedAssistant;
              newState.currentAssistantId = combinedAssistant.id;
              newState.currentAssistantTitle = combinedAssistant.name;
              console.log(
                "🎯 [AssistantStore] Set as current assistant:",
                combinedAssistant.name
              );
            }

            console.log(
              `📊 [AssistantStore] Store now has ${updatedAssistants.length} assistants`
            );

            return newState;
          });

          return combinedAssistant;
        } catch (error) {
          console.error("Error in createAssistantWithConfig:", error);
          set({ error: error.message });
          throw error;
        } finally {
          set({ isLoading: false });
        }
      },
      updateAssistantTools: async (assistantId, toolsOrUpdateData) => {
        try {
          set({ isLoading: true, error: null });

          const currentWorkspaceId = useAuthStore.getState().currentWorkspaceId;
          const userId = useAuthStore.getState().userId;
          if (!currentWorkspaceId || !userId) {
            throw new Error(
              `Missing required data: currentWorkspaceId: ${currentWorkspaceId}, userId: ${userId}`
            );
          }

          let targetAssistant = null;

          targetAssistant = get().assistants.find((a) => a.id === assistantId);

          if (!targetAssistant && get().currentAssistant) {
            const current = get().currentAssistant;
            if (current.id === assistantId) {
              targetAssistant = current;
            }
          }

          if (!targetAssistant) {
            throw new Error(
              `Assistant with ID ${assistantId} not found in store`
            );
          }

          let tools;
          let providedToolNames = null;
          let extraUpdateData = {};

          if (Array.isArray(toolsOrUpdateData)) {
            tools = toolsOrUpdateData;
          } else if (typeof toolsOrUpdateData === "object") {
            if (Array.isArray(toolsOrUpdateData.tools)) {
              tools = toolsOrUpdateData.tools;
            } else {
              tools = [];
            }

            if (Array.isArray(toolsOrUpdateData.tool_names)) {
              providedToolNames = toolsOrUpdateData.tool_names;
            } else if (typeof toolsOrUpdateData.tool_names === "string") {
              try {
                providedToolNames = JSON.parse(toolsOrUpdateData.tool_names);
              } catch (e) {
                console.warn("Failed to parse tool_names string:", e);
                providedToolNames = null;
              }
            }

            const { tools: _, tool_names: __, ...rest } = toolsOrUpdateData;
            extraUpdateData = rest;

            if (
              extraUpdateData.metadata &&
              Array.isArray(extraUpdateData.metadata.tool_names)
            ) {
              extraUpdateData.metadata = {
                ...extraUpdateData.metadata,
                tool_names: JSON.stringify(extraUpdateData.metadata.tool_names),
              };
            }
          } else {
            throw new Error(
              "Invalid tools format: must be array or object with tools field"
            );
          }

          if (!tools || !Array.isArray(tools)) {
            console.warn("Invalid tools format:", tools);
            tools = [];
          }

          const toolNames = tools
            .map((tool) =>
              tool.type === "function" ? tool.function?.name : tool.type
            )
            .filter(Boolean);

          const finalToolNames = providedToolNames || toolNames;

          const supabaseId = targetAssistant.id;

          if (!supabaseId) {
            throw new Error(
              "No valid Supabase ID found for this assistant. Cannot update tools."
            );
          }

          const combinedMetadata = {
            ...targetAssistant.metadata,
            updated_at: new Date().toISOString(),
            has_tools: tools.length > 0 ? "true" : "false",
            tool_count: tools.length.toString(),
            tool_names: Array.isArray(finalToolNames)
              ? JSON.stringify(finalToolNames)
              : finalToolNames,
            ...(extraUpdateData.metadata || {}),
          };

          try {
            const { data: result, error: updateError } = await supabase
              .from("assistants")
              .update({
                updated_at: new Date().toISOString(),
                tools: tools,
                tool_names: finalToolNames,
                metadata: combinedMetadata,
                ...extraUpdateData,
              })
              .eq("id", supabaseId)
              .select()
              .single();

            if (updateError) {
              console.error(
                "Error updating assistant in Supabase:",
                updateError
              );
              throw updateError;
            }

            const combinedAssistant = {
              ...result,
              tools: tools,
            };

            set((state) => {
              const updatedAssistants = state.assistants.map((ast) =>
                ast.id === supabaseId ? combinedAssistant : ast
              );

              let updatedCurrentAssistant = state.currentAssistant;
              if (
                state.currentAssistant &&
                state.currentAssistant.id === supabaseId
              ) {
                updatedCurrentAssistant = combinedAssistant;
              }

              return {
                assistants: updatedAssistants,
                currentAssistant: updatedCurrentAssistant,
                assistantTools: combinedAssistant.tools,
                currentAssistantId: supabaseId,
                currentAssistantTitle: combinedAssistant.name,
              };
            });

            return combinedAssistant;
          } catch (dbError) {
            console.error("Database operation error:", dbError);
            throw dbError;
          }
        } catch (error) {
          console.error("Error updating assistant tools:", error);
          set({ error: error.message });
          throw error;
        } finally {
          set({ isLoading: false });
        }
      },
      updateAssistant: async (assistantId, updatedAssistant) => {
        if (!assistantId) {
          console.error("Error in updateAssistant: assistantId is required");
          return null;
        }

        if (!updatedAssistant) {
          console.error(
            "Error in updateAssistant: updatedAssistant is required"
          );
          return null;
        }

        try {
          const isMetadataOnlyUpdate =
            Object.keys(updatedAssistant).length === 1 &&
            updatedAssistant.metadata &&
            Object.keys(updatedAssistant).every((key) => key === "metadata");

          if (isMetadataOnlyUpdate) {
            const existingAssistant = get().assistants.find(
              (a) => a.id === assistantId
            );

            if (!existingAssistant) {
              console.error(
                `Error in updateAssistant: Assistant with ID ${assistantId} not found`
              );
              return null;
            }

            if (
              updatedAssistant.metadata &&
              updatedAssistant.metadata.user_id &&
              (existingAssistant.user_id !==
                updatedAssistant.metadata.user_id ||
                !existingAssistant.metadata?.user_id ||
                existingAssistant.metadata?.user_id !==
                  updatedAssistant.metadata.user_id)
            ) {
              let updatedMetadata = existingAssistant.metadata || {};
              if (typeof updatedMetadata === "string") {
                try {
                  updatedMetadata = JSON.parse(updatedMetadata);
                } catch (e) {
                  console.error("Error parsing metadata string:", e);
                  updatedMetadata = {};
                }
              }

              updatedMetadata.user_id = updatedAssistant.metadata.user_id;
              updatedMetadata.updated_at = new Date().toISOString();

              const { data: updatedAssistantData, error: updateError } =
                await supabase
                  .from("assistants")
                  .update({
                    user_id: updatedAssistant.metadata.user_id,
                    metadata: updatedMetadata,
                    updated_at: new Date().toISOString(),
                  })
                  .eq("id", existingAssistant.id)
                  .select()
                  .single();

              if (updateError) {
                console.error(
                  "Error updating assistant with user ID:",
                  updateError
                );
              } else {
                return updatedAssistantData;
              }
            }

            return existingAssistant;
          }

          const { userId, currentWorkspaceId } = useAuthStore.getState();

          if (!userId) {
            const { user } = useAuthStore.getState();
            if (user?.id) {
              console.warn(
                "userId not available, using user.id instead:",
                user.id
              );
            } else {
              console.error("Cannot update assistant without userId");
              throw new Error(
                "User ID is required to update an assistant. Please ensure you're logged in."
              );
            }
          }

          const userIdToUse = userId || useAuthStore.getState().user?.id;

          const existingAssistant = get().assistants.find(
            (a) => a.id === assistantId
          );

          const isModelUpdate =
            updatedAssistant?.model &&
            existingAssistant?.model &&
            updatedAssistant.model !== existingAssistant.model;

          const isDeepseekModel = updatedAssistant?.model?.includes("deepseek");
          const wasDeepseekModel =
            existingAssistant?.model?.includes("deepseek");

          const isGeminiModel = updatedAssistant?.model?.includes("gemini");
          const wasGeminiModel = existingAssistant?.model?.includes("gemini");

          const isMistralModel = updatedAssistant?.model?.includes("mistral");
          const wasMistralModel = existingAssistant?.model?.includes("mistral");

          const isAnthropicModel = updatedAssistant?.model?.includes("claude");
          const wasAnthropicModel =
            existingAssistant?.model?.includes("claude");

          const isPerplexityModel =
            updatedAssistant?.model?.includes("perplexity");
          const wasPerplexityModel =
            existingAssistant?.model?.includes("perplexity");

          const isCohereModel = updatedAssistant?.model?.includes("command");
          const wasCohereModel = existingAssistant?.model?.includes("command");

          const isGroqModel =
            updatedAssistant?.model?.includes("gemma") ||
            updatedAssistant?.model?.includes("llama") ||
            updatedAssistant?.model?.includes("mixtral") ||
            (updatedAssistant?.model?.includes("mistral") && !isMistralModel);
          const wasGroqModel =
            existingAssistant?.model?.includes("gemma") ||
            existingAssistant?.model?.includes("llama") ||
            existingAssistant?.model?.includes("mixtral") ||
            (existingAssistant?.model?.includes("mistral") && !wasMistralModel);

          const isOpenAIModel =
            updatedAssistant?.model?.includes("gpt") ||
            updatedAssistant?.model?.includes("o1") ||
            updatedAssistant?.model?.includes("o3") ||
            updatedAssistant?.model?.includes("chatgpt");

          const wasOpenAIModel =
            existingAssistant?.model?.includes("gpt") ||
            existingAssistant?.model?.includes("o1") ||
            existingAssistant?.model?.includes("o3") ||
            existingAssistant?.model?.includes("chatgpt");

          const isModelSwitch =
            isDeepseekModel !== wasDeepseekModel ||
            isGeminiModel !== wasGeminiModel ||
            isMistralModel !== wasMistralModel ||
            isAnthropicModel !== wasAnthropicModel ||
            isGroqModel !== wasGroqModel ||
            isOpenAIModel !== wasOpenAIModel ||
            isPerplexityModel !== wasPerplexityModel ||
            isCohereModel !== wasCohereModel;

          const newProvider = isDeepseekModel
            ? "deepseek"
            : isGeminiModel
              ? "gemini"
              : isMistralModel
                ? "mistral"
                : isAnthropicModel
                  ? "anthropic"
                  : isGroqModel
                    ? "groq"
                    : isPerplexityModel
                      ? "perplexity"
                      : isCohereModel
                        ? "cohere"
                        : "openai";

          const updatedLocalAssistant = {
            ...existingAssistant,
            ...updatedAssistant,
            metadata: {
              ...(existingAssistant?.metadata || {}),
              ...(updatedAssistant?.metadata || {}),
              updated_at: new Date().toISOString(),
              provider: newProvider,
              model_provider: newProvider,
              workspace_id:
                currentWorkspaceId || existingAssistant?.metadata?.workspace_id,
              user_id: userIdToUse || existingAssistant?.metadata?.user_id,
            },
          };

          const supabaseId = existingAssistant?.id;

          if (supabaseId) {
            try {
              const supabaseUpdateData = {
                name:
                  updatedAssistant.name !== undefined
                    ? updatedAssistant.name
                    : existingAssistant?.name,
                description:
                  updatedAssistant.description !== undefined
                    ? updatedAssistant.description
                    : existingAssistant?.description,
                instructions:
                  updatedAssistant.instructions !== undefined
                    ? updatedAssistant.instructions
                    : existingAssistant?.instructions,
                model: updatedAssistant?.model || existingAssistant?.model,
                workspace_id:
                  currentWorkspaceId || existingAssistant?.workspace_id,
                updated_at: new Date().toISOString(),
                metadata: {
                  ...(existingAssistant?.metadata || {}),
                  ...(updatedAssistant?.metadata || {}),
                  updated_at: new Date().toISOString(),
                  provider: newProvider,
                  model_provider: newProvider,
                  workspace_id:
                    currentWorkspaceId ||
                    existingAssistant?.metadata?.workspace_id,
                  user_id: userIdToUse || existingAssistant?.metadata?.user_id,
                },
              };

              if (updatedAssistant.tools) {
                supabaseUpdateData.tools = updatedAssistant.tools;
                const toolNames = updatedAssistant.tools
                  .map((tool) => {
                    if (tool.type === "function") {
                      return tool.function?.name;
                    }
                    return tool.type;
                  })
                  .filter(Boolean);
                supabaseUpdateData.tool_names = toolNames;
              }

              const { data: updatedSupabaseAssistant, error } = await supabase
                .from("assistants")
                .update(supabaseUpdateData)
                .eq("id", supabaseId)
                .select()
                .single();

              if (error) {
                console.error("Error updating Supabase assistant:", error);
                set({ supabaseUpdateError: error.message });
              } else {
                set({ supabaseUpdateError: null });

                set((state) => ({
                  assistants: state.assistants.map((ast) =>
                    ast.id === supabaseId ? updatedSupabaseAssistant : ast
                  ),
                  currentAssistant:
                    state.currentAssistant &&
                    state.currentAssistant.id === supabaseId
                      ? updatedSupabaseAssistant
                      : state.currentAssistant,
                }));

                return updatedSupabaseAssistant;
              }
            } catch (supabaseError) {
              console.error(
                "Exception updating Supabase assistant:",
                supabaseError
              );
              set({ supabaseUpdateError: supabaseError.message });
            }
          }

          set((state) => ({
            assistants: state.assistants.map((ast) =>
              ast.id === existingAssistant.id ? updatedLocalAssistant : ast
            ),
            currentAssistant:
              state.currentAssistant &&
              state.currentAssistant.id === existingAssistant.id
                ? updatedLocalAssistant
                : state.currentAssistant,
          }));

          return updatedLocalAssistant;
        } catch (error) {
          console.error("Error in updateAssistant:", error);
          set({ error: error.message });
          throw error;
        }
      },

      getCurrentAssistantModelConfig: () => {
        const provider = "openai";
        const model = get().currentAssistant?.model;

        // No model is expected when logged out or no assistant selected
        if (!model) {
          return null;
        }

        try {
          const modelConfig = useModelStore
            .getState()
            .getModelById(provider, model);
          return modelConfig;
        } catch (error) {
          console.error("Error getting model config:", error);
          return null;
        }
      },
      deleteAssistant: async (assistantId) => {
        try {
          set({ isLoading: true, error: null });

          if (get().assistants.length <= 1) {
            throw new Error("You're required to have at least one assistant");
          }

          const assistantToDelete = get().assistants.find(
            (a) => a.id === assistantId
          );

          if (!assistantToDelete) {
            throw new Error(`Assistant with ID ${assistantId} not found`);
          }

          const supabaseId = assistantToDelete.id;

          if (supabaseId) {
            const deleteResult =
              await SupabaseAssistantUtils.deleteAssistant(supabaseId);

            if (!deleteResult) {
              console.warn(
                "Supabase deletion failed, but proceeding with state update"
              );
            }
          }

          set((state) => ({
            assistants: state.assistants.filter((a) => a.id !== assistantId),
            ...(state.currentAssistant?.id === assistantId
              ? {
                  currentAssistant: null,
                  currentAssistantId: null,
                  currentAssistantTitle: null,
                }
              : {}),
          }));

          return true;
        } catch (error) {
          console.error("Error deleting assistant:", error);
          set({ error: error.message });
          throw error;
        } finally {
          set({ isLoading: false });
        }
      },

      cleanupOrphanedAssistants: async () => {
        try {
          const orphans = get().assistants.filter(
            (a) =>
              a.metadata?.inline_creation === "true" &&
              a.name === "Untitled Assistant"
          );
          if (orphans.length === 0) return;

          for (const orphan of orphans) {
            try {
              // Skip the "must have at least one assistant" guard by
              // checking if there are enough non-orphan assistants
              const nonOrphanCount = get().assistants.filter(
                (a) =>
                  !(
                    a.metadata?.inline_creation === "true" &&
                    a.name === "Untitled Assistant"
                  )
              ).length;
              if (nonOrphanCount < 1) break;

              await get().deleteAssistant(orphan.id);
            } catch {
              // Ignore individual deletion failures
            }
          }
        } catch {
          // Best-effort cleanup — don't disrupt app initialization
        }
      },

      getAssistantsByWorkspace: (workspaceId) => {
        const { assistants, workspaceAssistants } = get();

        if (workspaceAssistants.has(workspaceId)) {
          const assistantIds = workspaceAssistants.get(workspaceId);
          return assistants
            .filter((assistant) => assistantIds.includes(assistant.id))
            .map((assistant) => get().reconstructAssistantTools(assistant));
        }

        return assistants
          .filter(
            (assistant) =>
              assistant &&
              Array.isArray(assistant.workspace_ids) &&
              assistant.workspace_ids.includes(workspaceId)
          )
          .map((assistant) => get().reconstructAssistantTools(assistant));
      },

      reconstructAssistantTools: (assistant) => {
        // Assistants already come with proper tools from the API
        return assistant;
      },

      resetStore: () => {
        set(INITIAL_STATE);
      },
    }),
    {
      name: STORAGE_KEY,
      partialize: (state) => {
        // Exclude initialization flags - they should always start fresh on page load
        const { initialized, initializing, error, ...rest } = state;
        return {
          ...rest,
          vectorStores: state.vectorStores
            ? Array.from(state.vectorStores.entries())
            : [],
          vectorStoreFiles: state.vectorStoreFiles
            ? Array.from(state.vectorStoreFiles.entries())
            : [],
          files: state.files ? Array.from(state.files.entries()) : [],
          assistantVectorStores: state.assistantVectorStores
            ? Array.from(state.assistantVectorStores.entries())
            : [],
          workspaceAssistants: state.workspaceAssistants
            ? Array.from(state.workspaceAssistants.entries())
            : [],
        };
      },
      onRehydrateStorage: () => (state) => {
        if (state) {
          // Always reset initialization flags on page load to ensure fresh data fetch
          state.initialized = false;
          state.initializing = false;
          state.error = null;

          if (Array.isArray(state.workspaceAssistants)) {
            state.workspaceAssistants = new Map(state.workspaceAssistants);
          } else {
            state.workspaceAssistants = new Map();
          }
        }
      },
    }
  )
);

export default useAssistantStore;
